import './UserGuide.css';

/**
 * User Guide — Project Management.
 *
 * CONTENT CLONED from `ai_readiness_template/views/user-guide.ejs`, the "Program Management"
 * card (lines 909-996). That section describes exactly this module: it opens with Roadmap
 * Definition and Timeline Finalization, then methodology and work items, budget and
 * resources, deployment tracking and status reporting — the same five steps in the same
 * order, and its prose is carried across verbatim rather than paraphrased.
 *
 * DESIGN CLONED from Inventory Registry's own User Guide, which the owner named as the
 * pattern to follow (2026-08-11: "Use APR module for both of these as a reference … This
 * should be the same design"). Same TOC, same cards, same tick-lists.
 *
 * THE CLASS PREFIX IS `pmug-`, NOT `ug-`. Inventory Registry's stylesheet defines 47 unscoped
 * `.ug-*` rules, and stylesheets are global once bundled — a second file defining the same
 * names would fight it, and whichever imported last would win silently. Its own prefix also
 * means editing one guide can never restyle the other (CLAUDE.md §1.2.1b).
 *
 * The source's "Need Additional Help?" block is not repeated here: this module has its own
 * Help & Support screen, which is where that lives and which the nav links to.
 */

interface FeatureCard {
  icon: string;
  title: string;
  body: string;
  points?: string[];
}

interface Section {
  id: string;
  icon: string;
  label: string;
  heading: string;
  intro?: string;
  features?: FeatureCard[];
  steps?: { n: string; title: string; desc: string }[];
}

/** Every string below is the source's own, from the Program Management card. */
const SECTIONS: Section[] = [
  {
    id: 'overview',
    icon: '🚀',
    label: 'Overview',
    heading: 'Project Management',
    intro:
      'Program Management turns the approved roadmap into a managed delivery program. Work ' +
      'flows top to bottom: define delivery projects, set up methodology, streams and work ' +
      'items, plan budget and resources, then track everything in the Execution Control ' +
      'Center. Open it from the sidebar or the Start Menu.',
    steps: [
      { n: '1', title: 'Project Creation', desc: 'Group opportunities into delivery projects and lock the timeline.' },
      { n: '2', title: 'Methodology & Work Items', desc: 'Choose Waterfall or Agile, define streams, work items, assignments and RACI.' },
      { n: '3', title: 'Budget & Resources', desc: 'Set the per-phase budget and resourcing baselines.' },
      { n: '4', title: 'Deployment Tracking', desc: 'Run delivery from the Execution Control Center and track budget and resources.' },
      { n: '5', title: 'Status Reporting', desc: 'Prepare templates, run weekly meetings, generate SteerCo decks.' },
    ],
  },
  {
    id: 'roadmap-definition',
    icon: '🗺️',
    label: 'Project Creation',
    heading: '1 · Project Creation',
    features: [
      {
        icon: 'workspaces',
        title: 'Project Creation',
        body:
          'Group In-Development opportunities into delivery projects — the unit of execution. ' +
          'A delivery project can span several opportunities.',
        points: [
          'Create a delivery project with an ID and description',
          'Assign opportunities to it; view Table or Gantt breakdown',
        ],
      },
      {
        icon: 'event',
        title: 'Timeline Finalization',
        body: 'Set one project-level start date and finalize (lock) the delivery timeline.',
        points: [
          'Pick the project start date',
          'Finalize to lock phase windows for execution',
        ],
      },
    ],
  },
  {
    id: 'work-items',
    icon: '🧩',
    label: 'Methodology & Work Items',
    heading: '2 · Methodology & Work Items',
    features: [
      {
        icon: 'rule',
        title: 'Methodology (per opportunity)',
        body:
          'Choose Waterfall or Agile per opportunity on Project Creation — the work item set ' +
          "and structure (phases vs. MVP waves) adapt to each opportunity's choice.",
      },
      {
        icon: 'hub',
        title: 'Stream Definition',
        body:
          'Select business-function streams (O2C, P2P, R2R, Development, …) and staff each with ' +
          'team members (name, email, role).',
        points: [
          'Add/remove custom streams and roles (shared across all projects)',
          'Team members feed the Work Item Assignment picker',
        ],
      },
      {
        icon: 'inventory_2',
        title: 'Work Item Definition',
        body:
          'Pick the work items produced in each phase. A work item can be Global (one) or ' +
          'per-stream (one instance per chosen stream, e.g. Functional Spec × O2C/P2P/R2R).',
        points: [
          'Set proposed "delivered by" dates and document links',
          'Add custom work items; per-stream items multiply the count',
        ],
      },
      {
        icon: 'how_to_reg',
        title: 'Work Item Assignment',
        body:
          'Assign people to each in-scope work item: pick a stream, then type or select a ' +
          'member; set location (On-Site / Off-Shore / Internal) and assignment dates ' +
          '(proposed phase-start → project-end).',
      },
      {
        icon: 'grid_on',
        title: 'RACI Definition',
        body:
          'Define Responsible, Accountable, Consulted, Informed roles per work item, with ' +
          'sensible defaults you can adjust and custom roles.',
      },
    ],
  },
  {
    id: 'budget-resources',
    icon: '💰',
    label: 'Budget & Resources',
    heading: '3 · Budget & Resources',
    features: [
      {
        icon: 'account_balance_wallet',
        title: 'Budget Plan',
        body:
          'Define the per-phase budget baseline. Proposed values come from the approved cost ' +
          "model (summed across the project's opportunities); override any phase manually.",
      },
      {
        icon: 'groups',
        title: 'Resource Plan',
        body:
          'Define the per-phase resourcing baseline (FTE). Expand a phase to override FTE per ' +
          'role; the phase total rolls up from the roles.',
      },
    ],
  },
  {
    id: 'tracking',
    icon: '📊',
    label: 'Deployment Tracking',
    heading: '4 · Deployment Tracking',
    features: [
      {
        icon: 'insights',
        title: 'Execution Control Center',
        body: 'The day-to-day cockpit for a delivery project, with tabs:',
        points: [
          'Overview — KPI cards (completion, work item status, overdue, risks, next milestone, budget & resource utilization), progress by phase, risks by severity',
          'Gantt Chart — phase-per-row timeline with % complete, a "today" line, and program summary (start, end, duration, business days left)',
          'Calendar — phase bands, work item due dates, milestones (★), and meeting sessions',
          'Details — set per-work-item progress (slider, auto-saved), status, dates, notes',
          'Risk — risk register with owner, due date, mitigation, resolution and dated notes',
          'Governance — meetings & milestones with Outlook-style recurrence (weekly/bi-weekly/monthly, weekday selection, start/end dates) that expand into sessions',
        ],
      },
      {
        icon: 'payments',
        title: 'Budget Tracker',
        body:
          'Track budget utilization per phase — planned (from Budget Plan) vs. actual. Capture ' +
          'actuals as a quick per-phase number or an itemized vendor payment ledger (vendor, ' +
          'invoice #, payment date, amount).',
      },
      {
        icon: 'groups_2',
        title: 'Resource Tracker',
        body:
          'Track resource utilization as effort (person-days) — planned (FTE × phase ' +
          'working-days) vs. an effort log of entries (resource, start date, duration days, FTE).',
      },
    ],
  },
  {
    id: 'status-reporting',
    icon: '📝',
    label: 'Status Reporting',
    heading: '5 · Status Reporting',
    intro:
      'Prepare report templates, run weekly status meetings, and generate Steering Committee ' +
      'decks from live execution data.',
  },
];

export function UserGuide() {
  return (
    <div className="pm-uguide">
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">
              User Guide<span className="pmug-badge">v2.0</span>
            </h1>
            <p className="page-header__subtitle">
              Complete guide to running a delivery program
            </p>
          </div>
          <div className="page-header__actions" />
        </div>
      </div>

      <div className="pmug-toc">
        <p className="pmug-toc-title">Quick Navigation</p>
        <ul className="pmug-toc-list">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>
                <span aria-hidden="true">{s.icon}</span>
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {SECTIONS.map((s) => (
        <section key={s.id} id={s.id} className="pmug-card">
          <h2>{s.heading}</h2>
          {s.intro ? <p>{s.intro}</p> : null}

          {s.steps ? (
            <div className="pmug-steps">
              {s.steps.map((q) => (
                <div className="pmug-step" key={q.n}>
                  <div className="pmug-step-n">{q.n}</div>
                  <div className="pmug-step-t">{q.title}</div>
                  <div className="pmug-step-d">{q.desc}</div>
                </div>
              ))}
            </div>
          ) : null}

          {s.features ? (
            <div className="pmug-features">
              {s.features.map((f) => (
                <div className="pmug-feature" key={f.title}>
                  <div className="pmug-feature-hd">
                    <i className="material-icons">{f.icon}</i>
                    <h3>{f.title}</h3>
                  </div>
                  <p>{f.body}</p>
                  {f.points ? (
                    <ul className="pmug-ticks">
                      {f.points.map((p) => (
                        <li key={p}>
                          <i className="material-icons">check_circle</i>
                          {p}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}
